/**
 * @file language-labels.ts
 *
 * Produces the autonym (a language's name in its own language) for the account
 * panel's language switcher, e.g. `en-GB` -> "English", `de-DE` -> "Deutsch".
 * Uses the built-in CLDR `Intl.DisplayNames` (no extra data or dependency, the
 * same API the shipping region localization uses), capitalizing the first letter
 * so locales whose autonym is lowercased (e.g. French "français") still read as a
 * title. Results are memoized since the supported set is small and fixed.
 */

import type { SupportedLocale } from '@wsb/i18n';

const cache = new Map<string, string>();

/**
 * Returns the display label (autonym) for a locale.
 *
 * @param locale - A supported admin-UI locale (e.g. `de-DE`).
 *
 * @returns The language's own name, first letter capitalized; the locale code
 *   itself as a last resort when the runtime cannot resolve a name.
 */
export function languageLabel(locale: SupportedLocale): string {
  const cached = cache.get(locale);
  if (cached) {
    return cached;
  }
  const primarySubtag = locale.split('-')[0];
  const name =
    new Intl.DisplayNames([locale], { type: 'language' }).of(primarySubtag) ??
    locale;
  const label = name.charAt(0).toUpperCase() + name.slice(1);
  cache.set(locale, label);
  return label;
}
